import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useClient } from 'jsonapi-react';
import { questionsApi } from '@/services/questions';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { FiEye } from 'react-icons/fi';
import { QuestionDetailContent, MODALITY_LABEL, ETAPA_LABEL } from '@/components/custom/question/question-detail-content';
import QuestionsFilters from '@/pages/Admin/Questions/QuestionsFilters';

const PER_PAGE = 25;

// Mesmas colunas do banco de questões + coluna de seleção na frente e "Ver" no fim.
const TABLE_HEAD = [{ label: '', width: 44 }, { label: 'Código', width: 120 }, { label: 'Tipo', width: 120 }, { label: 'Etapa', width: 80 }, { label: 'Matéria', width: 150 }, { label: 'Enunciado' }, { label: '', width: 84 }];

// Heurística de "matéria" idêntica à do banco (QuestionsList).
const MATERIAS = ['Matemática', 'Língua Portuguesa', 'Lingua Portuguesa'];

const strip = html =>
  String(html || '')
    .replace(/<[^>]+>/g, '')
    .trim();

function truncate(text = '', n = 100) {
  const clean = strip(text);
  return clean.length > n ? `${clean.slice(0, n - 1)}…` : clean;
}

function materiaOf(attrs) {
  return (attrs.taxonomies || []).find(t => MATERIAS.includes(t.name))?.name || '—';
}

/**
 * Normaliza um item JSON:API no shape do modal: metadados de exibição
 * (id, description, aiGenerated), o objeto cru (raw) pro preview/colunas, e a
 * formQuestion pronta pro form do wizard.
 * JSON:API: comando em attributes.description; textos de apoio inline em
 * attributes['motivator-texts'] (N:N); alternativas inline com is-correct.
 */
function mapItem(q) {
  const attrs = q.attributes || {};
  const motivatorBody = (attrs['motivator-texts'] || [])
    .map(t => strip(t.body))
    .filter(Boolean)
    .join('\n');
  const command = strip(attrs.description);
  const aiGenerated = Boolean(attrs['ai-generated']);
  return {
    id: q.id,
    raw: q, // objeto JSON:API cru — alimenta o preview (QuestionDetailContent) e as colunas
    description: command,
    aiGenerated,
    formQuestion: {
      question_id: q.id,
      ai_generated: aiGenerated,
      description: [motivatorBody, command].filter(Boolean).join('\n\n'),
      question_type: 'multiple_choice',
      taxonomy_id: '',
      alternatives: (attrs.alternatives || []).map(alt => ({
        description: strip(alt.description),
        is_correct: Boolean(alt['is-correct'])
      }))
    }
  };
}

function AiBadge() {
  return (
    <span
      style={{
        display: 'inline-block',
        marginRight: 6,
        padding: '1px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        background: '#eef2ff',
        color: '#4338ca'
      }}
    >
      IA
    </span>
  );
}

/**
 * Modal para importar questões do banco aprovadas pelo pedagógico para um Desafio ao Vivo.
 * Lista só current_review_status=aprovado e devolve cada seleção já no shape de questão
 * do form (description, question_type, alternatives com is_correct), pronta pra virar
 * card editável — como se o professor tivesse preenchido à mão.
 *
 * Reusa a barra de filtros (QuestionsFilters) e o preview (QuestionDetailContent) do banco
 * de questões. A leitura de "aprovado" é regra fixa do modal e nunca vira filtro editável.
 * A seleção é acumulada por id, então trocar filtro/página não perde o que já foi marcado.
 * Renderiza via portal no body pra ficar acima da sidebar do wizard.
 *
 * Props:
 *   open           {boolean}            - controla visibilidade
 *   onClose        {function}           - chamado ao fechar sem importar
 *   onConfirm      {function}           - chamado com array de questões no shape do form
 *   areaTaxonomyId {string|number|null} - id da taxonomia de área (ENEM); pré-seleciona Matéria. Opcional.
 */
export default function ImportQuestionsModal({ open, onClose, onConfirm, areaTaxonomyId = null }) {
  const client = useClient();
  const [taxonomies, setTaxonomies] = useState([]);
  const [filters, setFilters] = useState({});
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState([]); // itens completos, na ordem de seleção
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [facets, setFacets] = useState(null); // contagem por opção de filtro (pills)
  const [descritorIds, setDescritorIds] = useState([]); // ids dos descritores exibidos (pro facets)
  const [previewId, setPreviewId] = useState(null); // questão aberta no painel de preview

  // Ao abrir: zera a seleção e ancora os filtros (matéria da área ENEM, se veio).
  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setFilters(areaTaxonomyId ? { 'taxonomies.id': [areaTaxonomyId] } : {});
  }, [open, areaTaxonomyId]);

  // Matérias pro filtro (mesmo endpoint do banco de questões).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    client
      .fetch('taxonomies/used_by_questions')
      .then(r => {
        if (!cancelled) setTaxonomies(r?.data || []);
      })
      .catch(() => {
        if (!cancelled) setTaxonomies([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Busca reativa (página 1) ao abrir ou quando os filtros mudam.
  // review_status=aprovado é sempre forçado — é regra do modal, não filtro.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuestions([]);
    setPage(1);
    const effective = { ...filters, review_status: 'aprovado' };
    questionsApi
      .index({ filters: effective, page: 1, perPage: PER_PAGE })
      .then(res => {
        if (cancelled) return;
        const items = (res?.data || []).map(mapItem);
        setQuestions(items);
        setHasMore(items.length >= PER_PAGE);
      })
      .catch(() => {
        if (!cancelled) setError('Erro ao carregar questões. Tente novamente.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Facetas (número em cada pill). Recalcula ao abrir, mudar filtro, chegar a lista de
  // matérias ou mudar o conjunto de descritores exibidos. review_status=aprovado fixo.
  const taxOptionIdsKey = taxonomies.map(t => t.id).join(',');
  const descritorIdsKey = descritorIds.join(',');
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const effective = { ...filters, review_status: 'aprovado' };
    questionsApi
      .facets({
        filters: effective,
        taxonomyOptionIds: taxonomies.map(t => t.id),
        descritorOptionIds: descritorIds
      })
      .then(res => {
        if (!cancelled) setFacets(res || null);
      })
      .catch(() => {
        if (!cancelled) setFacets(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, filtersKey, taxOptionIdsKey, descritorIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadMore() {
    const next = page + 1;
    setLoadingMore(true);
    const effective = { ...filters, review_status: 'aprovado' };
    questionsApi
      .index({ filters: effective, page: next, perPage: PER_PAGE })
      .then(res => {
        const items = (res?.data || []).map(mapItem);
        setQuestions(prev => [...prev, ...items]);
        setHasMore(items.length >= PER_PAGE);
        setPage(next);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }

  const isSelected = id => selected.some(s => s.id === id);

  function toggle(item) {
    setSelected(prev => (prev.some(s => s.id === item.id) ? prev.filter(s => s.id !== item.id) : [...prev, item]));
  }

  if (!open) return null;

  const previewItem = questions.find(q => q.id === previewId)?.raw;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Importar questões do banco"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: 24,
          width: '95vw',
          maxWidth: 1280,
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 16
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Importar questões do banco</h2>

        <QuestionsFilters
          filters={filters}
          onChange={setFilters}
          taxonomies={taxonomies}
          counts={facets}
          onDescritoresChange={setDescritorIds}
        />

        <div
          style={{
            flex: 1,
            overflow: 'auto',
            borderTop: '1px solid #f0f0f0',
            paddingTop: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 12
          }}
        >
          {loading && <p>Carregando questões...</p>}

          {error && (
            <p
              role="alert"
              style={{ color: '#b91c1c' }}
            >
              {error}
            </p>
          )}

          {!loading && !error && questions.length === 0 && <p>Nenhuma questão aprovada encontrada.</p>}

          {!loading && !error && questions.length > 0 && (
            <>
              <Table className="min-w-[900px]">
                <TableHeaderWrapper tableHead={TABLE_HEAD} />
                <TableBody>
                  {questions.map(q => {
                    const a = q.raw?.attributes || {};
                    return (
                      <TableRow key={q.id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected(q.id)}
                            onChange={() => toggle(q)}
                            aria-label={q.description}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs text-neutral">{a['external-id'] || a.code || `#${q.id}`}</TableCell>
                        <TableCell>
                          <Badge
                            variant="neutral-lightest"
                            size="sm"
                          >
                            {MODALITY_LABEL[a.modality] || a.modality || '—'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {a.etapa ? (
                            <Badge
                              variant="default"
                              size="sm"
                            >
                              {ETAPA_LABEL[a.etapa] || a.etapa}
                            </Badge>
                          ) : (
                            <span className="text-xs text-neutral-light">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{materiaOf(a)}</TableCell>
                        <TableCell className="text-sm">
                          {q.aiGenerated && <AiBadge />}
                          {truncate(q.description, 100)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="xs"
                            onClick={() => setPreviewId(q.id)}
                          >
                            <ButtonIcon>
                              <FiEye />
                            </ButtonIcon>
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {hasMore && (
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </Button>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#6b7280' }}>{selected.length > 0 ? `${selected.length} selecionada${selected.length > 1 ? 's' : ''}` : ''}</span>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="default"
              size="md"
              onClick={() => onConfirm(selected.map(s => s.formQuestion))}
              disabled={selected.length === 0}
            >
              Importar {selected.length > 0 ? `(${selected.length})` : ''}
            </Button>
          </div>
        </div>
      </div>

      {/* Preview lateral — mesmo componente do banco de questões */}
      <Sheet
        open={!!previewId}
        onOpenChange={o => !o && setPreviewId(null)}
      >
        <SheetContent
          side="right"
          className="flex w-full max-w-2xl flex-col gap-0 overflow-hidden p-0"
        >
          {previewItem && (
            <>
              <QuestionDetailContent question={previewItem} />
              <div className="flex-shrink-0 border-t border-neutral-lightest bg-white px-6 py-3">
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewId(null)}
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>,
    document.body
  );
}
